import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18n/LanguageContext';
import { useHomeT } from '../i18n/strings';
import { portfolioItems, PRODUCT_HOME_KEYS, localizeItem } from '../data/portfolio';
import ProjectCard from '../components/ProjectCard';
import Badge from '../components/Badge';
import CasePanel from '../components/CasePanel';

export default function HomePage() {
  const { lang } = useLanguage();
  const t = useHomeT(lang);
  const [openKey, setOpenKey] = React.useState(null);

  const products = PRODUCT_HOME_KEYS.map((k) => portfolioItems.find((i) => i.key === k)).filter(Boolean);
  const openItem = portfolioItems.find((i) => i.key === openKey) || null;

  React.useEffect(() => {
    if (window.location.hash === '#case-fieldledger') setOpenKey('fieldledger');
  }, []);

  return (
    <>
      <Helmet>
        <title>GDWD — Products &amp; operational platforms</title>
        <meta
          name="description"
          content="GDWD designs and engineers production software — products, operational platforms, mobile apps, and long-term systems for education and public partners."
        />
      </Helmet>

      <section id="top" className="gdwd-hero">
        <div className="gdwd-hero__glow" />
        <div className="gdwd-hero__grid">
          <div>
            <div className="gdwd-kicker">{`// ${t.kicker}`}</div>
            <h1 className="gdwd-h1">
              {t.heroTitle}
              <span>.</span>
            </h1>
            <p className="gdwd-lead">{t.heroSub}</p>
            <div className="gdwd-hero__ctas">
              <Link to="/work" className="gdwd-btn gdwd-btn--primary gdwd-btn--lg">
                {t.heroCta1} →
              </Link>
              <a href="#products" className="gdwd-btn gdwd-btn--ghost gdwd-btn--lg">
                {t.heroCta2}
              </a>
            </div>
            <div className="gdwd-trust">
              <strong>{t.trustedBy}:</strong> {t.trustList}
            </div>
          </div>
          <div>
            <div className="gdwd-console" aria-hidden>
              <div className="gdwd-console__bar">
                <span className="gdwd-console__dot" style={{ background: '#FF5F57' }} />
                <span className="gdwd-console__dot" style={{ background: '#FEBC2E' }} />
                <span className="gdwd-console__dot" style={{ background: '#28C840' }} />
                <span className="gdwd-console__title">gdwd — operations</span>
              </div>
              <div className="gdwd-console__body">
                {[
                  ['ClassReflect', t.consoleLive, true],
                  ['School MS — UK', t.consoleSchools, true],
                  ['FullCourt', t.consoleLive, true],
                  ['Trukka', t.consoleWip, false],
                  ['FieldLedger', t.consoleWip, false],
                ].map(([name, status, live]) => (
                  <div className="gdwd-console__row" key={name}>
                    <span>
                      <span style={{ color: live ? 'var(--live)' : 'var(--wip)' }}>{live ? '●' : '◐'}</span> {name}
                    </span>
                    <span className="gdwd-console__muted">{status}</span>
                  </div>
                ))}
                <div>
                  <span className="gdwd-console__caret" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="products" className="gdwd-section">
        <div className="gdwd-container">
          <div className="gdwd-section__head">
            <span className="gdwd-section__num">01</span>
            <h2 className="gdwd-h2">{t.productsTitle}</h2>
          </div>
          <p className="gdwd-section__lead">{t.productsLead}</p>
          <div className="gdwd-grid-3">
            {products.map((p) => (
              <ProjectCard key={p.key} item={p} onOpen={setOpenKey} />
            ))}
          </div>
        </div>
      </section>

      <section id="platforms" className="gdwd-section">
        <div className="gdwd-container">
          <div className="gdwd-section__head">
            <span className="gdwd-section__num">02</span>
            <h2 className="gdwd-h2">{t.platformsTitle}</h2>
          </div>
          <p className="gdwd-section__lead">{t.platformsLead}</p>
          <div className="gdwd-grid-2" style={{ marginBottom: 20 }}>
            {['fullcourt'].map((k) => {
              const item = portfolioItems.find((i) => i.key === k);
              return item ? <ProjectCard key={k} item={item} onOpen={setOpenKey} /> : null;
            })}
          </div>

          <div className="gdwd-uk">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
                <Badge kind="Platform">{t.badgePlatform}</Badge>
                <span className="gdwd-note" style={{ color: 'var(--console-muted)' }}>
                  {t.ukTag}
                </span>
              </div>
              <h3>{t.ukTitle}</h3>
              <p>{t.ukBlurb}</p>
              <div className="gdwd-uk__bullets">
                {[t.ukB1, t.ukB2, t.ukB3, t.ukB4].map((b) => (
                  <div key={b}>
                    <span>→</span>
                    {b}
                  </div>
                ))}
              </div>
              <div className="gdwd-uk__note">
                <span style={{ color: 'var(--sky)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>→</span> {t.ukAttendanceNote} —{' '}
                <a href="#mobile">{t.seeMobile} ↓</a>
              </div>
            </div>
            <div>
              <div className="gdwd-uk__logos-label">{t.ukLogosLabel}</div>
              <div className="gdwd-uk__logos">
                <div className="gdwd-uk__logo">
                  <img src="/assets/logos/lsst.png" alt="LSST" />
                </div>
                <div className="gdwd-uk__logo">
                  <img src="/assets/logos/fsb.png" alt="Fairfield School of Business" />
                </div>
                <div className="gdwd-uk__logo">
                  <img src="/assets/logos/ukbc-web-logo.png" alt="UKBC" onError={(e) => { e.currentTarget.src = '/assets/logos/ukbc.png'; }} />
                </div>
              </div>
              <div className="gdwd-uk__caption">{t.ukNote}</div>
            </div>
          </div>
        </div>
      </section>

      <section id="mobile" className="gdwd-section">
        <div className="gdwd-container">
          <div className="gdwd-section__head">
            <span className="gdwd-section__num">03</span>
            <h2 className="gdwd-h2">{t.mobileTitle}</h2>
          </div>
          <p className="gdwd-section__lead">{t.mobileLead}</p>
          <div className="gdwd-grid-2">
            <div className="gdwd-mobile-card">
              <div className="gdwd-phone">
                <img src="/assets/mobile/trukka-map.png" alt="Trukka" />
              </div>
              <div>
                <div className="gdwd-card__meta">
                  <Badge kind="Product">{t.badgeProduct}</Badge>
                  <Badge kind="Mobile">{t.badgeMobile}</Badge>
                  <Badge kind="Wip">{t.badgeWip}</Badge>
                </div>
                <h3 className="gdwd-card__title" style={{ fontSize: 24 }}>
                  Trukka
                </h3>
                <p className="gdwd-card__blurb" style={{ fontSize: 15 }}>
                  {t.trukkaBlurb}
                </p>
                <div className="gdwd-chips">
                  <span className="gdwd-chip">Web</span>
                  <span className="gdwd-chip">iOS · Android</span>
                  <span className="gdwd-chip">{t.chipMaps}</span>
                </div>
                <button type="button" className="gdwd-card__cta" style={{ marginTop: 16, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => setOpenKey('trukka')}>
                  {t.details} →
                </button>
              </div>
            </div>
            <div className="gdwd-mobile-card">
              <div className="gdwd-phone">
                <img src="/assets/mobile/attendance-classes.png" alt="College Attendance" />
              </div>
              <div>
                <div className="gdwd-card__meta">
                  <Badge kind="Mobile">{t.badgeMobile}</Badge>
                  <span className="gdwd-note">UK COLLEGES</span>
                </div>
                <h3 className="gdwd-card__title" style={{ fontSize: 24 }}>
                  College Attendance
                </h3>
                <p className="gdwd-card__blurb" style={{ fontSize: 15 }}>
                  {t.attendanceBlurb}
                </p>
                <div className="gdwd-chips">
                  <span className="gdwd-chip">Android</span>
                  <span className="gdwd-chip">Multi-college</span>
                </div>
                <button type="button" className="gdwd-card__cta" style={{ marginTop: 16, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => setOpenKey('attendance')}>
                  {t.details} →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="collaborations" className="gdwd-section">
        <div className="gdwd-container">
          <div className="gdwd-section__head">
            <span className="gdwd-section__num">04</span>
            <h2 className="gdwd-h2">{t.collabTitle}</h2>
          </div>
          <p className="gdwd-section__lead">{t.collabLead}</p>
          <div className="gdwd-grid-2">
            <button type="button" className="gdwd-collab-card" style={{ cursor: 'pointer', textAlign: 'left', width: '100%', font: 'inherit', color: 'inherit', border: '1px solid var(--hairline)' }} onClick={() => setOpenKey('lusiic')}>
              <div className="gdwd-card__meta">
                <Badge kind="Collaboration">{t.badgeCollab}</Badge>
              </div>
              <div className="gdwd-collab-card__logos">
                <img src="/assets/logos/lu-university.svg" alt="University of Latvia" />
              </div>
              <h3 className="gdwd-card__title">LUSIIC</h3>
              <p className="gdwd-card__blurb">{t.lusiicBlurb}</p>
            </button>
            <button type="button" className="gdwd-collab-card" style={{ cursor: 'pointer', textAlign: 'left', width: '100%', font: 'inherit', color: 'inherit', border: '1px solid var(--hairline)' }} onClick={() => setOpenKey('luogre')}>
              <div className="gdwd-card__meta">
                <Badge kind="Collaboration">{t.badgeCollab}</Badge>
              </div>
              <div className="gdwd-collab-card__logos">
                <img src="/assets/logos/lu-university.svg" alt="LU" />
                <img src="/assets/logos/ogre-novads.png" alt="Ogre municipality" />
                <img src="/assets/logos/onizglitiba.png" alt="On-izglītība" />
              </div>
              <h3 className="gdwd-card__title">{t.eventsTitle}</h3>
              <p className="gdwd-card__blurb">{t.eventsBlurb}</p>
            </button>
          </div>
          <div className="gdwd-earlier-label">{t.earlierLabel}</div>
          {['signet', 'everlasting'].map((k) => {
            const raw = portfolioItems.find((i) => i.key === k);
            const item = localizeItem(raw, lang);
            return (
              <button type="button" className="gdwd-earlier-row" key={k} onClick={() => setOpenKey(k)}>
                <img src={item.src} alt="" />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
                    <strong style={{ fontFamily: 'var(--font-display)', fontSize: 17 }}>{item.title}</strong>
                    <span className="gdwd-past-pill">{t.pastWork}</span>
                  </div>
                  <div style={{ color: 'var(--slate-600)', fontSize: 14 }}>{k === 'signet' ? t.signetBlurb : t.ebBlurb}</div>
                </div>
                <span className="gdwd-card__cta">{t.details} →</span>
              </button>
            );
          })}
        </div>
      </section>

      <section id="next" className="gdwd-section">
        <div className="gdwd-container">
          <div className="gdwd-section__head">
            <span className="gdwd-section__num">05</span>
            <h2 className="gdwd-h2">{t.nextTitle}</h2>
          </div>
          <p className="gdwd-section__lead">{t.nextLead}</p>
          <div className="gdwd-fieldledger">
            <button
              type="button"
              className="gdwd-fieldledger__media"
              onClick={() => setOpenKey('fieldledger')}
              aria-label="FieldLedger screenshots"
            >
              <img
                src="/assets/portfolio/fieldledger/05-map.jpg"
                alt="FieldLedger map — field boundaries"
                loading="lazy"
                decoding="async"
              />
            </button>
            <div>
              <div className="gdwd-card__meta">
                <Badge kind="Product">{t.badgeProduct}</Badge>
                <Badge kind="Wip">{t.badgeWip}</Badge>
                <span className="gdwd-note">Web · 14 photos</span>
              </div>
              <h3 className="gdwd-card__title" style={{ fontSize: 28 }}>
                FieldLedger
              </h3>
              <p className="gdwd-card__blurb" style={{ fontSize: 15.5 }}>
                {t.fieldledgerBlurb}
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a className="gdwd-btn gdwd-btn--ink" href="mailto:info@gdwd.lv">
                  {t.pilotCta} →
                </a>
                <button type="button" className="gdwd-btn gdwd-btn--ghost" style={{ fontSize: 14, padding: '12px 18px' }} onClick={() => setOpenKey('fieldledger')}>
                  {t.moreDetails} →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="gdwd-section">
        <div className="gdwd-container">
          <div className="gdwd-section__head">
            <span className="gdwd-section__num">06</span>
            <h2 className="gdwd-h2">{t.aboutTitle}</h2>
          </div>
          <div className="gdwd-about-grid">
            <div>
              <p style={{ color: 'var(--slate-600)', marginTop: 0 }}>{t.aboutP1}</p>
              <p style={{ color: 'var(--slate-600)' }}>{t.aboutP2}</p>
            </div>
            <div className="gdwd-process">
              {[
                [t.step1, t.step1d],
                [t.step2, t.step2d],
                [t.step3, t.step3d],
                [t.step4, t.step4d],
              ].map(([title, desc]) => (
                <div className="gdwd-process__tile" key={title}>
                  <h4>{title}</h4>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="gdwd-contact" style={{ scrollMarginTop: 'var(--nav-h)' }}>
        <h2>
          {t.contactTitle}
          <span>?</span>
        </h2>
        <p>{t.contactLead}</p>
        <a className="gdwd-btn gdwd-btn--primary gdwd-btn--lg" href="mailto:info@gdwd.lv">
          {t.contactCta} →
        </a>
        <div className="gdwd-contact__links">
          <a href="mailto:info@gdwd.lv">info@gdwd.lv</a>
          <a href="https://wa.me/37129438427" target="_blank" rel="noopener noreferrer">
            WhatsApp
          </a>
          <a
            href="https://www.linkedin.com/in/guntars-dikis-a85b6042/"
            target="_blank"
            rel="noopener noreferrer"
          >
            LinkedIn
          </a>
          <a href="https://github.com/guntarsdikis" target="_blank" rel="noopener noreferrer">
            GitHub
          </a>
        </div>
      </section>

      <CasePanel item={openItem} onClose={() => setOpenKey(null)} />
    </>
  );
}
